import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Mail, Trash2, CheckCheck, Clock, Phone, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

/**
 * Floating chat bubble — pinned bottom-right across the whole workspace.
 * Shows unread messages (contact-form submissions), with live push via the
 * `inbox:ws-event` custom-event bus owned by RequestsKanban. We don't open
 * our own WebSocket to avoid two sockets fighting for the same token.
 */
function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return "az önce";
  if (diff < 60) return `${Math.round(diff)} dk önce`;
  if (diff < 1440) return `${Math.round(diff / 60)} sa önce`;
  try { return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}

export default function MessagesChatBubble({ hideOnPaths = [] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const audioRef = useRef(null);
  const loc = useLocation();
  const path = loc.pathname || "";
  const hidden = hideOnPaths.some((p) => path === p || path.startsWith(p + "/"));

  const { data = [], refetch, isFetching } = useQuery({
    queryKey: ["admin", "messages"],
    queryFn: () => api.get("/admin/messages").then((r) => r.data),
    refetchInterval: open ? 15000 : 60000,
  });

  const live = data.filter((m) => !m.deleted);
  const unread = live.filter((m) => !m.read);

  // Listen for WS events dispatched by RequestsKanban. Any new message triggers
  // a flash + chime + list refresh so the bubble pulses even when closed.
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (!msg || msg.type !== "new") return;
      if (msg.kind !== "message") return;
      setFlashId(msg.item?.id);
      qc.invalidateQueries({ queryKey: ["admin", "messages"] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
      // play soft chime if we have one — otherwise just vibrate (mobile)
      try { navigator.vibrate?.(80); } catch {}
      setTimeout(() => setFlashId(null), 4000);
    };
    window.addEventListener("inbox:ws-event", handler);
    return () => window.removeEventListener("inbox:ws-event", handler);
  }, [qc]);

  const mark = async (m, read) => {
    try { await api.patch(`/admin/messages/${m.id}`, { read }); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const trash = async (m) => {
    if (!window.confirm("Mesaj çöpe atılsın mı?")) return;
    try { await api.delete(`/admin/messages/${m.id}`); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const node = (
    <div className="fixed bottom-5 right-5 z-[55] flex flex-col items-end gap-2" data-testid="messages-chat-bubble">
      {/* Panel */}
      {open && (
        <div className="w-[22rem] max-w-[calc(100vw-2.5rem)] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-indigo-100 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4" data-testid="chat-bubble-panel">
          <div className="px-4 py-3 flex items-center gap-2 text-white"
               style={{ background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 55%,#c026d3 100%)" }}>
            <MessageCircle size={18} />
            <div className="flex-1">
              <div className="text-sm font-bold">Mesajlar</div>
              <div className="text-[11px] opacity-80">
                {unread.length > 0 ? `${unread.length} okunmamış` : "Tümü okundu"}
              </div>
            </div>
            <button onClick={() => refetch()} className="p-1 rounded hover:bg-white/20" title="Yenile">
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            </button>
            <Link to="/admin/workspace/mesajlar" onClick={() => setOpen(false)} className="text-[11px] px-2 py-1 rounded bg-white/15 hover:bg-white/25 font-medium">
              Tümü
            </Link>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/20" data-testid="chat-bubble-close">
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gradient-to-b from-slate-50 to-white">
            {live.length === 0 && (
              <div className="text-center text-sm text-stone-400 italic p-8">
                <MessageCircle className="mx-auto mb-2 opacity-30" size={28} />
                Henüz mesaj yok.
              </div>
            )}
            {live.slice(0, 30).map((m) => {
              const isFlash = flashId === m.id;
              return (
                <div
                  key={m.id}
                  className={`rounded-xl p-2.5 border transition
                    ${m.read ? "bg-white border-stone-200" : "bg-indigo-50/60 border-indigo-200 shadow-sm"}
                    ${isFlash ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
                  data-testid={`chat-msg-${m.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 ${m.read ? "bg-stone-400" : "bg-gradient-to-br from-indigo-500 to-violet-600"}`}>
                      {(m.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-xs truncate flex-1 ${m.read ? "text-stone-700" : "font-semibold text-slate-900"}`}>
                          {m.name || "(isimsiz)"}
                        </p>
                        <span className="text-[10px] text-stone-400 shrink-0">{fmtRel(m.created_at)}</span>
                      </div>
                      <div className="text-[11px] text-stone-500 truncate">{m.email || m.phone}</div>
                      {m.subject && <div className="text-[11px] font-medium text-stone-700 mt-0.5">{m.subject}</div>}
                      <p className="text-xs text-stone-600 line-clamp-2 mt-1">{m.message || m.area || "—"}</p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <a href={`mailto:${m.email}`} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                          <Mail size={9} /> Yanıtla
                        </a>
                        {!m.read ? (
                          <button onClick={() => mark(m, true)} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border hover:bg-stone-50" data-testid={`chat-mark-read-${m.id}`}>
                            <CheckCheck size={9} /> Okundu
                          </button>
                        ) : (
                          <button onClick={() => mark(m, false)} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border hover:bg-stone-50">
                            Okunmadı
                          </button>
                        )}
                        <button onClick={() => trash(m)} className="text-[10px] ml-auto p-0.5 rounded text-stone-400 hover:text-red-600">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bubble button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-14 h-14 rounded-full text-white shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        style={{ background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 55%,#c026d3 100%)" }}
        data-testid="chat-bubble-toggle"
        aria-label="Mesajlar"
      >
        <MessageCircle size={24} />
        {unread.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[11px] font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1 border-2 border-white animate-in zoom-in" data-testid="chat-bubble-badge">
            {unread.length > 99 ? "99+" : unread.length}
          </span>
        )}
      </button>
    </div>
  );

  return hidden ? null : (typeof document !== "undefined" ? createPortal(node, document.body) : null);
}
